import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  ExternalLink,
  Instagram,
  Music,
  Facebook,
  Twitter,
  Youtube,
  Linkedin,
  Heart,
  MessageCircle,
  Share2,
  Eye,
  Search,
  ChevronDown,
  Calendar,
  DollarSign,
  X,
  CheckSquare,
  Square,
  Asterisk,
  List,
  LayoutGrid,
  RefreshCw,
  MousePointerClick,
  Navigation
} from 'lucide-react';
import PostCard from './PostCard';
import { Card, Button } from '../ui';
import Tooltip from '../ui/Tooltip';
import useSocialMediaStore from '../../stores/socialMediaStore';
import { formatEMV } from '../../utils/emvCalculator';
import { SocialMediaPost } from '../../types/socialMedia';
import {
  TopPost,
  TopPostsResponse,
  socialMediaService
} from '../../services/socialMediaService';
import { toastService, getErrorMessage } from '../../services/toastService';
import useGlobalCustomerStore from '../../stores/globalCustomerStore';
import { useDrawerNavigation } from '../../contexts/DrawerNavigationContext';
import { FilterBuilder } from '../filters/FilterBuilder';
import { FilterCondition, FilterConfig } from '../filters/types';

const PostsTable: React.FC = () => {
  const { openPostInsights } = useDrawerNavigation();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'emv' | 'likes' | 'views' | 'comments' | 'shares' | 'date'>('emv');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage] = useState(32); // Set posts per page (4-wide grid → 8 full rows)
  const [apiPosts, setApiPosts] = useState<TopPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalPosts, setTotalPosts] = useState(0);

  // Views editing state
  const [editingViews, setEditingViews] = useState<string | null>(null);
  const [editViewsValue, setEditViewsValue] = useState<string>('');

  // Likes editing state
  const [editingLikes, setEditingLikes] = useState<string | null>(null);
  const [editLikesValue, setEditLikesValue] = useState<string>('');

  // Multi-select mode state
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedPostIds, setSelectedPostIds] = useState<Set<string>>(new Set());

  const [viewMode, setViewMode] = useState<'table' | 'grid'>('grid');
  // One-time onboarding hint pointing users at the new view toggle.
  const [showViewHint, setShowViewHint] = useState(false);

  // Refresh state
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Filter state
  const [filterConditions, setFilterConditions] = useState<FilterCondition[]>([]);

  const {
    posts,
    selectedPlatform,
    selectedProfile,
    selectedPostType,
    currentCustomerId,
    currentPeriod,
    selectedPlatformIds,
    selectedAccountIds,
    customDateRange,
    accountType
  } = useSocialMediaStore();
  
  const { getAPIFilterParams } = useGlobalCustomerStore();

  // Filter configuration
  const availableFilters: FilterConfig[] = [
    {
      field: 'isSponsored',
      label: 'Sponsored',
      operators: [
        { value: '=', label: '=' },
        { value: '!=', label: '!=' }
      ],
      valueOptions: [
        { value: true, label: 'True' },
        { value: false, label: 'False' }
      ],
      valueType: 'boolean'
    },
    {
      field: 'isAd',
      label: 'Is Ad',
      operators: [
        { value: '=', label: '=' },
        { value: '!=', label: '!=' }
      ],
      valueOptions: [
        { value: true, label: 'True' },
        { value: false, label: 'False' }
      ],
      valueType: 'boolean'
    },
    {
      field: 'hasCollaborators',
      label: 'Has Collaborators',
      operators: [
        { value: '=', label: '=' },
        { value: '!=', label: '!=' }
      ],
      valueOptions: [
        { value: true, label: 'True' },
        { value: false, label: 'False' }
      ],
      valueType: 'boolean'
    }
  ];

  // Get platform icon based on platform name or ID
  const getPlatformIcon = (platformName: string) => {
    const lowerPlatform = platformName.toLowerCase();
    switch (lowerPlatform) {
      case 'instagram': return Instagram;
      case 'tiktok': return Music;
      case 'facebook': return Facebook;
      case 'twitter': case 'x': return Twitter;
      case 'youtube': return Youtube;
      case 'linkedin': return Linkedin;
      default: return Instagram;
    }
  };

  // Get platform brand color
  const getPlatformColor = (platformName: string) => {
    const lowerPlatform = platformName.toLowerCase();
    switch (lowerPlatform) {
      case 'instagram': return 'text-pink-500';
      case 'tiktok': return 'text-black';
      case 'facebook': return 'text-blue-600';
      case 'twitter': case 'x': return 'text-blue-400';
      case 'youtube': return 'text-red-600';
      case 'linkedin': return 'text-blue-700';
      default: return 'text-gray-500';
    }
  };

  // Reset to page 1 when search term or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterConditions]);

  // Scroll to top when page changes
  useEffect(() => {
    const scrollToTop = () => {
      // Try multiple scroll methods for better compatibility
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;

      // Also try smooth scroll as backup
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    };

    scrollToTop();
  }, [currentPage]);

  // Create a reusable loadPosts function
  const loadPosts = useCallback(async () => {
    if (!currentCustomerId) {
      setApiPosts([]);
      return;
    }

    try {
      setLoading(true);

      // Get global account filter params
      const globalFilterParams = getAPIFilterParams();
      const hasGlobalAccountSelection = (globalFilterParams.accountIds?.length ?? 0) > 0;

      const params: any = {
        customerId: currentCustomerId,
        period: currentPeriod,
        page: currentPage,
        limit: postsPerPage,
        sortBy: sortBy,
        sortOrder: sortOrder,
        ...globalFilterParams
      };

      if (selectedPlatformIds && selectedPlatformIds.length > 0) {
        params.platformIds = selectedPlatformIds;
      }

      if (hasGlobalAccountSelection) {
        // specific account selected globally — no category filter
      } else if (selectedAccountIds && selectedAccountIds.length > 0) {
        params.accountIds = selectedAccountIds;
      } else if (accountType !== 'all') {
        params.categoryId = accountType === 'owned' ? 1 : 2;
      }

      if (selectedPostType && selectedPostType !== 'all') {
        params.postTypes = [selectedPostType];
      }

      if (currentPeriod === 'custom' && customDateRange) {
        params.customStartDate = customDateRange.start;
        params.customEndDate = customDateRange.end;
      }

      if (searchTerm && searchTerm.trim()) {
        params.searchTerm = searchTerm.trim();
      }

      // Apply filter conditions
      filterConditions.forEach(condition => {
        if (condition.operator === '=') {
          params[condition.field] = condition.value;
        } else if (condition.operator === '!=') {
          // For != operator, we pass the opposite value for boolean fields
          // This is a simple implementation - may need refinement based on backend API
          if (typeof condition.value === 'boolean') {
            params[condition.field] = !condition.value;
          }
        }
      });

      const response: TopPostsResponse = await socialMediaService.getTopPosts(params);
      if (response && response.data && response.data.posts) {
        setApiPosts(response.data.posts);
        setTotalPosts(response.data.pagination.totalPosts);
      }
    } catch (err) {
      try {
        const errorDetails = getErrorMessage(err);
        toastService.error(errorDetails.title, errorDetails.message || 'Failed to retrieve top performing posts');
      } catch (toastError) {
        console.error('Failed to show toast:', toastError);
      }
      setApiPosts([]);
    } finally {
      setLoading(false);
    }
  }, [currentCustomerId, currentPeriod, currentPage, postsPerPage, sortBy, sortOrder, selectedPlatformIds, selectedAccountIds, selectedPostType, customDateRange, searchTerm, filterConditions, accountType, getAPIFilterParams]);

  // Load posts from API when customer or filters change
  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  // Show the view-toggle onboarding hint once per user.
  useEffect(() => {
    if (!localStorage.getItem('posts_view_toggle_hint_seen')) {
      setShowViewHint(true);
    }
  }, []);

  const dismissViewHint = () => {
    setShowViewHint(false);
    localStorage.setItem('posts_view_toggle_hint_seen', 'true');
  };

  // Convert TopPost to SocialMediaPost format for compatibility
  const convertApiPostToSocialMediaPost = (apiPost: TopPost): SocialMediaPost => ({
    id: apiPost.id.toString(),
    platform: apiPost.platform.name as 'instagram' | 'tiktok',
    caption: apiPost.caption,
    authorName: apiPost.account.displayName,
    authorUsername: apiPost.collaboratorUsername || apiPost.account.username,
    url: apiPost.postUrl,
    likes: apiPost.metrics.likes,
    comments: apiPost.metrics.comments,
    shares: apiPost.metrics.shares,
    views: apiPost.metrics.views,
    timestamp: apiPost.postedAt,
    emv: typeof apiPost.metrics.emv === 'string'
      ? parseFloat(apiPost.metrics.emv.replace('$', '').replace(',', ''))
      : parseFloat(apiPost.metrics.emv) || 0,
    sponsor: apiPost.isSponsored ? ['Sponsored'] : undefined,
    sponsorshipTag: apiPost.isSponsored ? 'Sponsored Content' : undefined,
    thumbnailUrl: apiPost.account.profilePicUrl || undefined,
    hashtags: [],
    contentType: apiPost.contentType, // Add content type from API
    contentCategory: apiPost.contentCategory ?? null, // Scout content category (null if account not scouted)
    postType: apiPost.postType, // Add post type from API
    partitionKey: apiPost.partitionKey, // Add partition key from API
    hasOverrides: apiPost.metrics.hasOverrides || false,
    overrideMetadata: apiPost.metrics.overrideMetadata || null,
    extendedMetrics: apiPost.metrics.extendedMetrics || null
  });

  // Use API posts if customer is selected, otherwise fallback to local posts
  const postsToDisplay = currentCustomerId && apiPosts.length > 0 
    ? apiPosts.map(convertApiPostToSocialMediaPost)
    : posts;

  // Filter and sort posts
  const filteredAndSortedPosts = useMemo(() => {
    let filtered = postsToDisplay;

    // Apply platform filter
    if (selectedPlatform !== 'all') {
      filtered = filtered.filter(post => post.platform === selectedPlatform);
    }

    // Apply profile filter
    if (selectedProfile !== null) {
      filtered = filtered.filter(post => post.authorUsername === selectedProfile);
    }

    // Content type filtering is now handled at the API level, so we skip client-side filtering

    // Apply search filter only for non-API data (fallback mode)
    // When using API data, search is handled server-side
    if (searchTerm && !currentCustomerId) {
      filtered = filtered.filter(post => {
        const searchLower = searchTerm.toLowerCase();
        const sponsorMatch = post.sponsor ? (
          Array.isArray(post.sponsor)
            ? post.sponsor.some(s => s && s.toLowerCase().includes(searchLower))
            : post.sponsor.toLowerCase().includes(searchLower)
        ) : false;

        const captionMatch = post.caption && post.caption.toLowerCase().includes(searchLower);
        const authorNameMatch = post.authorName && post.authorName.toLowerCase().includes(searchLower);
        const authorUsernameMatch = post.authorUsername && post.authorUsername.toLowerCase().includes(searchLower);

        return captionMatch || authorNameMatch || authorUsernameMatch || sponsorMatch;
      });
    }

    return filtered;
  }, [postsToDisplay, selectedPlatform, selectedProfile, selectedPostType, searchTerm, sortOrder, currentCustomerId]);

  const handleSort = (field: 'emv' | 'likes' | 'views' | 'comments' | 'shares' | 'date') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };


  const openInsightsDrawer = (post: SocialMediaPost) => {
    openPostInsights(post);
  };

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  // Views editing handlers
  const handleViewsEdit = (postId: string, currentViews: number) => {
    setEditingViews(postId);
    setEditViewsValue(currentViews.toString());
  };

  const handleViewsCancel = () => {
    setEditingViews(null);
    setEditViewsValue('');
  };

  const handleViewsSave = async (postId: string, newViews: number) => {
    try {
      // Find the original views count from the current post data
      const post = filteredAndSortedPosts.find(p => p.id === postId);
      const originalViews = post?.views || 0;
      
      // Call the API to update the views
      await socialMediaService.updatePostViews(postId, newViews, originalViews);
      
      // Reset editing state first
      setEditingViews(null);
      setEditViewsValue('');
      
      // Refresh the posts data to show the updated value
      await loadPosts();
    } catch (error) {
      console.error('Failed to update views:', error);
      try {
        const errorDetails = getErrorMessage(error);
        toastService.error(errorDetails.title, errorDetails.message || 'Failed to update views');
      } catch (toastError) {
        console.error('Failed to show toast:', toastError);
      }
      // Reset editing state on error
      setEditingViews(null);
      setEditViewsValue('');
    }
  };

  // Likes editing handlers
  const handleLikesEdit = (postId: string, currentLikes: number) => {
    setEditingLikes(postId);
    setEditLikesValue(currentLikes.toString());
  };

  const handleLikesCancel = () => {
    setEditingLikes(null);
    setEditLikesValue('');
  };

  const handleLikesSave = async (postId: string, newLikes: number) => {
    try {
      const post = filteredAndSortedPosts.find(p => p.id === postId);
      const originalLikes = post?.likes || 0;
      await socialMediaService.updatePostLikes(postId, newLikes, originalLikes);
      setEditingLikes(null);
      setEditLikesValue('');
      await loadPosts();
    } catch (error) {
      console.error('Failed to update likes:', error);
      try {
        const errorDetails = getErrorMessage(error);
        toastService.error(errorDetails.title, errorDetails.message || 'Failed to update likes');
      } catch (toastError) {
        console.error('Failed to show toast:', toastError);
      }
      setEditingLikes(null);
      setEditLikesValue('');
    }
  };

  // Multi-select handlers
  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    setSelectedPostIds(new Set()); // Clear selections when toggling mode
  };

  const togglePostSelection = (postId: string) => {
    const newSelection = new Set(selectedPostIds);
    if (newSelection.has(postId)) {
      newSelection.delete(postId);
    } else {
      newSelection.add(postId);
    }
    setSelectedPostIds(newSelection);
  };

  const toggleSelectAll = () => {
    if (selectedPostIds.size === filteredAndSortedPosts.length) {
      setSelectedPostIds(new Set());
    } else {
      setSelectedPostIds(new Set(filteredAndSortedPosts.map(post => post.id)));
    }
  };

  const clearSelection = () => {
    setSelectedPostIds(new Set());
  };

  const handleRefresh = async () => {
    if (!currentCustomerId || isRefreshing) return;
    setIsRefreshing(true);
    try {
      await loadPosts();
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Search and Selection */}
      <Card className="p-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                type="text"
                placeholder="Search posts, authors, or sponsors..."
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                className="pl-8 pr-8 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <FilterBuilder
              availableFilters={availableFilters}
              conditions={filterConditions}
              onApply={setFilterConditions}
            />
            <button
              onClick={toggleSelectionMode}
              disabled={false}
              className={`inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                isSelectionMode
                  ? 'bg-avi-primary text-white border-avi-primary hover:bg-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {isSelectionMode ? (
                <>
                  <X className="h-3.5 w-3.5 mr-1.5" />
                  Cancel Selection
                </>
              ) : (
                <>
                  <CheckSquare className="h-3.5 w-3.5 mr-1.5" />
                  Select Posts
                </>
              )}
            </button>
            <Tooltip content="Refresh Posts" placement="top">
              <button
                onClick={handleRefresh}
                disabled={isRefreshing || !currentCustomerId}
                className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-40"
              >
                <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              </button>
            </Tooltip>
            <div className="relative">
              <div
                className={`flex items-center bg-gray-100 rounded-lg p-0.5 transition-shadow ${
                  showViewHint ? 'ring-2 ring-blue-400 ring-offset-1' : ''
                }`}
              >
                <Tooltip content="Card View" placement="top">
                  <button
                    onClick={() => { setViewMode('grid'); setIsSelectionMode(false); setSelectedPostIds(new Set()); }}
                    className={`p-1.5 rounded-md transition-colors ${
                      viewMode === 'grid'
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <LayoutGrid className="h-4 w-4" />
                  </button>
                </Tooltip>
                <Tooltip content="Table View (legacy)" placement="top">
                  <button
                    onClick={() => setViewMode('table')}
                    className={`p-1.5 rounded-md transition-colors ${
                      viewMode === 'table'
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <List className="h-4 w-4" />
                  </button>
                </Tooltip>
              </div>

              {/* One-time onboarding hint */}
              {showViewHint && (
                <div className="absolute right-0 top-full mt-2 z-50 w-64">
                  {/* arrow */}
                  <div className="absolute -top-1 right-4 h-2.5 w-2.5 rotate-45 bg-gray-900" />
                  <div className="relative bg-gray-900 text-white rounded-lg shadow-lg p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2">
                        <LayoutGrid className="h-4 w-4 mt-0.5 flex-shrink-0 text-blue-300" />
                        <p className="text-xs leading-relaxed">
                          New! Posts now open in <span className="font-semibold">card view</span>. Use this toggle to switch to the <span className="font-semibold">legacy table view</span> anytime.
                        </p>
                      </div>
                      <button
                        onClick={dismissViewHint}
                        className="text-gray-400 hover:text-white flex-shrink-0"
                        aria-label="Dismiss"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="mt-2 flex justify-end">
                      <button
                        onClick={dismissViewHint}
                        className="text-[11px] font-medium text-blue-300 hover:text-blue-200"
                      >
                        Got it
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>


      {viewMode === 'grid' && (
        <div className="relative">
          {loading && (
            <div className="absolute inset-0 bg-white/75 flex items-center justify-center z-10 rounded-xl">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-avi-primary"></div>
                <span className="text-gray-900">Loading posts...</span>
              </div>
            </div>
          )}
          {apiPosts.length === 0 && !loading ? (
            <Card className="p-12 text-center">
              <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No posts found</h3>
              <p className="text-gray-500">
                {searchTerm ? 'Try adjusting your filters or search terms.' : 'Upload some data to get started.'}
              </p>
            </Card>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {apiPosts.map((post) => (
                <PostCard
                  key={`${post.partitionKey}-${post.id}`}
                  post={post}
                  customerId={currentCustomerId || ''}
                  onClick={() => openInsightsDrawer(convertApiPostToSocialMediaPost(post))}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Posts Table */}
      {viewMode === 'table' && <Card className="overflow-hidden relative" data-posts-table>
        {loading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-avi-primary"></div>
              <span className="text-gray-900">Loading posts...</span>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-gray-200">
              <tr>
                {isSelectionMode && (
                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider w-12">
                    <button
                      onClick={toggleSelectAll}
                      className="text-gray-500 hover:text-gray-700 transition-colors"
                      aria-label="Select all posts"
                    >
                      {selectedPostIds.size === filteredAndSortedPosts.length && filteredAndSortedPosts.length > 0 ? (
                        <CheckSquare className="h-4 w-4" />
                      ) : (
                        <Square className="h-4 w-4" />
                      )}
                    </button>
                  </th>
                )}
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider w-16">
                  {/* No header - Platform & Type column */}
                </th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                  Post Title
                </th>
                <th
                  className="px-3 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                  onClick={() => handleSort('emv')}
                >
                  <div className="flex items-center space-x-1">
                    <span>EMV</span>
                    <ChevronDown className={`h-3 w-3 transition-transform ${
                      sortBy === 'emv' ? (sortOrder === 'desc' ? 'rotate-0' : 'rotate-180') : ''
                    }`} />
                  </div>
                </th>
                <th
                  className="px-3 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                  onClick={() => handleSort('likes')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Engagement</span>
                    <ChevronDown className={`h-3 w-3 transition-transform ${
                      sortBy === 'likes' ? (sortOrder === 'desc' ? 'rotate-0' : 'rotate-180') : ''
                    }`} />
                  </div>
                </th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                  Source
                </th>
                <th
                  className="px-3 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                  onClick={() => handleSort('date')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Post Date</span>
                    <ChevronDown className={`h-3 w-3 transition-transform ${
                      sortBy === 'date' ? (sortOrder === 'desc' ? 'rotate-0' : 'rotate-180') : ''
                    }`} />
                  </div>
                </th>
                <th className="px-3 py-2 text-right text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAndSortedPosts.map((post) => {
                const isSelected = selectedPostIds.has(post.id);
                return (
                  <motion.tr
                    key={post.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className={`cursor-pointer ${
                      isSelectionMode && isSelected
                        ? 'bg-blue-50 hover:bg-blue-100'
                        : post.emv >= 500
                          ? 'bg-green-50 hover:bg-green-100'
                          : post.emv >= 200
                            ? 'bg-yellow-50 hover:bg-yellow-100'
                            : 'bg-red-50 hover:bg-red-100'
                    }`}
                    onClick={() => {
                      if (isSelectionMode) {
                        togglePostSelection(post.id);
                      } else {
                        openInsightsDrawer(post);
                      }
                    }}
                  >
                    {isSelectionMode && (
                      <td className="px-3 py-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => togglePostSelection(post.id)}
                          className="text-gray-500 hover:text-gray-700 transition-colors"
                          aria-label={isSelected ? "Deselect post" : "Select post"}
                        >
                          {isSelected ? (
                            <CheckSquare className="h-4 w-4 text-avi-primary" />
                          ) : (
                            <Square className="h-4 w-4" />
                          )}
                        </button>
                      </td>
                    )}
                    <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex flex-col items-center justify-center space-y-1">
                      {post.platform === 'tiktok' ? (
                        <img
                          src="/tiktok-logo.png"
                          alt="TikTok"
                          className="h-4 w-4 rounded-sm"
                        />
                      ) : (
                        (() => {
                          const PlatformIcon = getPlatformIcon(post.platform);
                          const platformColor = getPlatformColor(post.platform);
                          return <PlatformIcon className={`h-4 w-4 ${platformColor}`} />;
                        })()
                      )}
                      <span className="px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-gray-100 text-gray-800 capitalize">
                        {post.postType || 'post'}
                      </span>
                      {post.contentCategory && (
                        <span className="px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-emerald-50 text-emerald-700 capitalize">
                          {post.contentCategory.replace(/_/g, ' ')}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div>
                      <p className="text-xs font-medium text-gray-900 line-clamp-1">
                        {post.caption}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        by {post.authorUsername}
                      </p>
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex items-center">
                      <DollarSign className="h-3 w-3 text-green-500 mr-1" />
                      <span className="text-xs font-semibold text-gray-900">
                        {formatEMV(post.emv)}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {post.postType === 'story' ? (
                      // Stories report Graph-API metrics, not likes/comments. Show link clicks,
                      // total navigations, and views (order: link_clicks, navigation, views).
                      <div className="flex items-center space-x-3 text-xs text-gray-500">
                        <div className="flex items-center" title="Link clicks">
                          <MousePointerClick className="h-3 w-3 mr-0.5" />
                          {formatNumber(post.extendedMetrics?.link_clicks ?? 0)}
                        </div>
                        <div className="flex items-center" title="Navigations (tap forward/back, exit, swipe)">
                          <Navigation className="h-3 w-3 mr-0.5" />
                          {formatNumber(post.extendedMetrics?.navigation?.total ?? 0)}
                        </div>
                        <div className="flex items-center" title="Views">
                          <Eye className="h-3 w-3 mr-0.5" />
                          {formatNumber(post.views)}
                        </div>
                      </div>
                    ) : (
                    <div className="flex items-center space-x-2 text-xs text-gray-500">
                      <div
                        className="flex items-center group"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Heart className="h-3 w-3 mr-0.5" />
                        {editingLikes === post.id ? (
                          <input
                            type="number"
                            value={editLikesValue}
                            onChange={(e) => {
                              e.stopPropagation();
                              setEditLikesValue(e.target.value);
                            }}
                            onKeyDown={(e) => {
                              e.stopPropagation();
                              if (e.key === 'Enter') {
                                handleLikesSave(post.id, parseInt(editLikesValue) || 0);
                              } else if (e.key === 'Escape') {
                                handleLikesCancel();
                              }
                            }}
                            onBlur={(e) => {
                              e.stopPropagation();
                              handleLikesSave(post.id, parseInt(editLikesValue) || 0);
                            }}
                            autoFocus
                            className="w-16 px-1 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-avi-primary"
                          />
                        ) : (
                          <span
                            className="cursor-pointer hover:bg-gray-100 px-1 py-0.5 rounded transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleLikesEdit(post.id, post.likes);
                            }}
                            title="Click to edit likes"
                          >
                            {formatNumber(post.likes)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center">
                        <MessageCircle className="h-3 w-3 mr-0.5" />
                        {formatNumber(post.comments)}
                      </div>
                      {post.shares > 0 && (
                        <div className="flex items-center">
                          <Share2 className="h-3 w-3 mr-0.5" />
                          {formatNumber(post.shares)}
                        </div>
                      )}
                      <div
                        className="flex items-center group"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Eye className="h-3 w-3 mr-0.5" />
                        {editingViews === post.id ? (
                          <input
                            type="number"
                            value={editViewsValue}
                            onChange={(e) => {
                              e.stopPropagation();
                              setEditViewsValue(e.target.value);
                            }}
                            onKeyDown={(e) => {
                              e.stopPropagation(); // Prevent event bubbling
                              if (e.key === 'Enter') {
                                handleViewsSave(post.id, parseInt(editViewsValue) || 0);
                              } else if (e.key === 'Escape') {
                                handleViewsCancel();
                              }
                            }}
                            onBlur={(e) => {
                              e.stopPropagation();
                              handleViewsSave(post.id, parseInt(editViewsValue) || 0);
                            }}
                            autoFocus
                            className="w-16 px-1 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-avi-primary"
                          />
                        ) : (
                          <span
                            className="cursor-pointer hover:bg-gray-100 px-1 py-0.5 rounded transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewsEdit(post.id, post.views);
                            }}
                            title="Click to edit views"
                          >
                            {formatNumber(post.views)}
                          </span>
                        )}
                      </div>
                      {post.hasOverrides && (
                        <span title={`Manually edited: ${post.overrideMetadata ? Object.keys(post.overrideMetadata).filter(k => post.overrideMetadata?.[k]).map(k => k.replace(/_count/g, '').replace(/_/g, ' ')).join(', ') : 'stats'}`}>
                          <Asterisk className="h-3 w-3 text-amber-500 ml-0.5 cursor-help" />
                        </span>
                      )}
                    </div>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {post.sponsor ? (
                      <div className="text-xs font-medium">
                        {Array.isArray(post.sponsor) ? (
                          <div className="space-y-0.5">
                            {post.sponsor.map((sponsor, index) => (
                              <div key={index} className="bg-indigo-100 text-indigo-800 px-1.5 py-0.5 rounded text-[10px] font-medium text-center">
                                {sponsor}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="bg-indigo-100 text-indigo-800 px-1.5 py-0.5 rounded text-[10px] font-medium text-center">
                            {post.sponsor}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="bg-gray-200 text-gray-800 px-1.5 py-0.5 rounded text-[10px] font-medium text-center">
                        Organic
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                    <div className="flex items-center">
                      <Calendar className="h-3 w-3 mr-1" />
                      {formatDate(post.timestamp)}
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium">
                    <div className="flex items-center justify-end space-x-0.5">
                      <div className="relative group">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openInsightsDrawer(post);
                          }}
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        <div className="absolute bottom-full right-0 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                          View AI Insights & Analysis
                        </div>
                      </div>
                      <div className="relative group">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(post.url, '_blank');
                          }}
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Button>
                        <div className="absolute bottom-full right-0 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                          View Original Post
                        </div>
                      </div>
                    </div>
                  </td>
                </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredAndSortedPosts.length === 0 && !loading && (
          <div className="text-center py-12">
            <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No posts found</h3>
            <p className="text-gray-500">
              {searchTerm
                ? 'Try adjusting your filters or search terms.'
                : 'Upload some data to get started.'}
            </p>
          </div>
        )}
      </Card>}

      {/* Results Summary and Pagination */}
      {filteredAndSortedPosts.length > 0 && (
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-500">
              {currentCustomerId && totalPosts > 0 ? (
                `Showing ${(currentPage - 1) * postsPerPage + 1}-${Math.min(currentPage * postsPerPage, totalPosts)} of ${totalPosts} posts`
              ) : (
                `Showing ${filteredAndSortedPosts.length} of ${postsToDisplay.length} posts`
              )}
            </div>
            
            {/* Pagination Controls */}
            {currentCustomerId && totalPosts > postsPerPage && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1 || loading}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                >
                  First
                </button>
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1 || loading}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                >
                  Previous
                </button>
                <span className="text-xs text-gray-600">
                  Page {currentPage} of {Math.ceil(totalPosts / postsPerPage)}
                </span>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage >= Math.ceil(totalPosts / postsPerPage) || loading}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                >
                  Next
                </button>
                <button
                  onClick={() => setCurrentPage(Math.ceil(totalPosts / postsPerPage))}
                  disabled={currentPage >= Math.ceil(totalPosts / postsPerPage) || loading}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                >
                  Last
                </button>
              </div>
            )}
          </div>
        </Card>
      )}


      {/* Bulk Action Bar */}
      {isSelectionMode && selectedPostIds.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <CheckSquare className="h-5 w-5 text-avi-primary" />
                <span className="text-sm font-medium text-gray-900">
                  {selectedPostIds.size} post{selectedPostIds.size !== 1 ? 's' : ''} selected
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={clearSelection}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                >
                  Clear Selection
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Post Insights Drawer */}
    </div>
  );
};


export default PostsTable;